import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Tooltip, Select, Tabs, Descriptions, Alert, Drawer, Empty, Statistic, Row, Col, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, FilterOutlined, ThunderboltOutlined, SyncOutlined, EyeOutlined, QuestionCircleOutlined, DashboardOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';
import { unwrapGoNull } from '../../../shared/utils/goNull';

const { TextArea } = Input;
const { TabPane } = Tabs;

// Direction options
const directionOptions = [
    { label: '入站', value: 'inbound' },
    { label: '出站', value: 'outbound' },
    { label: '双向', value: 'both' },
];

// Scope options
const scopeOptions = [
    { label: '全局', value: 'global' },
    { label: '域名', value: 'domain' },
    { label: '用户', value: 'user' },
];

// Limit type options for rate limit
const limitTypeOptions = [
    { label: 'IP 地址', value: 'ip' },
    { label: '发件人', value: 'sender' },
    { label: '域名', value: 'domain' },
    { label: '收件人', value: 'recipient' },
];

// Condition help text
const conditionHelp = `Arishem 规则引擎 JSON 格式，示例：
{
  "OpLogic": "&&",
  "Conditions": [
    {"Field": "$.from", "Op": "contains", "Value": "spam"},
    {"Field": "$.subject", "Op": "regex", "Value": "^\\[SPAM\\]"}
  ]
}

支持的字段: $.from, $.to, $.subject, $.headers, $.ip, $.size, $.attachments
支持的操作: eq, ne, contains, not_contains, regex, gt, lt, gte, lte, in, not_in`;

// Actions help text
const actionsHelp = `动作数组 JSON 格式，示例：
[
  {"type": "reject", "params": {"code": 550, "message": "Rejected by policy"}},
  {"type": "tag", "params": {"tag": "spam"}}
]

支持的动作类型:
- reject: 拒绝 (code, message)
- defer: 延迟 (code, message)
- quarantine: 隔离
- tag: 添加标签 (tag)
- modify_subject: 修改主题 (prefix)
- discard: 丢弃`;

const Rules = () => {
    const [activeTab, setActiveTab] = useState('filter');
    const [rules, setRules] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [viewingRule, setViewingRule] = useState(null);
    const [editingRule, setEditingRule] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    // Rate limit realtime stats
    const [rlStatsModalVisible, setRlStatsModalVisible] = useState(false);
    const [rlStatsRecord, setRlStatsRecord] = useState(null);
    const [rlStatsKey, setRlStatsKey] = useState('');
    const [rlStatsData, setRlStatsData] = useState(null);
    const [rlStatsLoading, setRlStatsLoading] = useState(false);

    // 获取集群列表
    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                // 选择默认集群或第一个集群
                const defaultCluster = list.find(c => c.is_default);
                if (defaultCluster) {
                    setSelectedCluster(defaultCluster.id);
                } else if (list.length > 0) {
                    setSelectedCluster(list[0].id);
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    const fetchRules = async (page = 1, pageSize = 20) => {
        if (!selectedCluster) return;
        setLoading(true);
        try {
            const endpoint = activeTab === 'filter' ? '/webmail/rules' : '/webmail/ratelimit';
            const res = await adminApi.get(endpoint, {
                params: { cluster_id: selectedCluster, page, per_page: pageSize }
            });
            if (res.data.success || res.data.code === 0) {
                const data = res.data.data || {};
                setRules(unwrapGoNull(data.rules || data.list || []));
                setPagination({
                    current: data.page || page,
                    pageSize: data.per_page || pageSize,
                    total: data.total || (data.rules || data.list || []).length,
                });
            } else {
                message.error(res.data.error || '获取规则列表失败');
            }
        } catch (error) {
            message.error('获取规则列表失败: ' + (error.response?.data?.error || error.message));
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchRules();
        }
    }, [selectedCluster, activeTab]);

    const handleAdd = () => {
        setEditingRule(null);
        form.resetFields();
        if (activeTab === 'filter') {
            form.setFieldsValue({
                enabled: true,
                priority: 100,
                scope: 'global',
                direction: 'inbound',
                stop_processing: false,
            });
        } else {
            form.setFieldsValue({
                enabled: true,
                priority: 100,
                scope: 'global',
                direction: 'inbound',
                limit_type: 'ip',
                limit_per_minute: 10,
                limit_per_hour: 100,
                limit_per_day: 500,
                block_duration: 3600,
            });
        }
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRule(record);
        const formValues = {
            ...record,
            description: record.description?.String || record.description || '',
            scope_value: record.scope_value?.String || record.scope_value || '',
            condition: typeof record.condition === 'object' ? JSON.stringify(record.condition, null, 2) : (record.condition || ''),
            actions: typeof record.actions === 'object' ? JSON.stringify(record.actions, null, 2) : (record.actions || ''),
            exceed_action: typeof record.exceed_action === 'object' ? JSON.stringify(record.exceed_action, null, 2) : (record.exceed_action || ''),
        };
        form.setFieldsValue(formValues);
        setModalVisible(true);
    };

    const handleView = (record) => {
        setViewingRule(record);
        setDetailDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const endpoint = activeTab === 'filter' ? `/webmail/rules/${id}` : `/webmail/ratelimit/${id}`;
            const res = await adminApi.delete(endpoint, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.success || res.data.code === 0) {
                message.success('删除成功');
                fetchRules(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const endpoint = activeTab === 'filter' ? '/webmail/rules' : '/webmail/ratelimit';

            // 解析 JSON 字段
            const data = { ...values, cluster_id: selectedCluster };
            if (values.condition) {
                try {
                    data.condition = JSON.parse(values.condition);
                } catch (e) {
                    message.error('条件 JSON 格式错误');
                    return;
                }
            }
            if (values.actions) {
                try {
                    data.actions = JSON.parse(values.actions);
                } catch (e) {
                    message.error('动作 JSON 格式错误');
                    return;
                }
            }
            if (values.exceed_action) {
                try {
                    data.exceed_action = JSON.parse(values.exceed_action);
                } catch (e) {
                    message.error('超限动作 JSON 格式错误');
                    return;
                }
            }

            if (editingRule) {
                const res = await adminApi.put(`${endpoint}/${editingRule.id}`, data);
                if (res.data.success || res.data.code === 0) {
                    message.success('更新成功');
                    setModalVisible(false);
                    fetchRules(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.error || '更新失败');
                }
            } else {
                const res = await adminApi.post(endpoint, data);
                if (res.data.success || res.data.code === 0) {
                    message.success('创建成功');
                    setModalVisible(false);
                    fetchRules(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.error || '创建失败');
                }
            }
        } catch (error) {
            message.error('操作失败: ' + (error.response?.data?.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleEnabled = async (record) => {
        const endpoint = activeTab === 'filter' ? `/webmail/rules/${record.id}` : `/webmail/ratelimit/${record.id}`;
        try {
            const res = await adminApi.put(endpoint, {
                ...record,
                enabled: !record.enabled,
                cluster_id: selectedCluster,
            });
            if (res.data.success || res.data.code === 0) {
                message.success(record.enabled ? '已禁用' : '已启用');
                fetchRules(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败');
        }
    };

    // Fetch realtime hit stats for a rate-limit key
    const fetchRateLimitStats = async (key) => {
        if (!key) {
            message.warning('请输入限速键');
            return;
        }
        setRlStatsLoading(true);
        try {
            const res = await adminApi.get(`/webmail/ratelimit/stats/${encodeURIComponent(key)}`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.success || res.data.code === 0) {
                setRlStatsData(res.data.data || {});
            } else {
                message.error(res.data.error || res.data.message || '获取实时命中失败');
            }
        } catch (error) {
            message.error('获取实时命中失败: ' + (error.response?.data?.error || error.message));
        }
        setRlStatsLoading(false);
    };

    const handleViewRateLimitStats = (record) => {
        setRlStatsRecord(record);
        setRlStatsData(null);
        const defaultKey = (record.scope_value?.String || record.scope_value || record.name || '').toString();
        setRlStatsKey(defaultKey);
        setRlStatsModalVisible(true);
        if (defaultKey) {
            fetchRateLimitStats(defaultKey);
        }
    };

    const handleSyncRules = async () => {
        setSyncing(true);
        try {
            const res = await adminApi.post('/webmail/rules/sync', {
                cluster_id: selectedCluster,
            });
            if (res.data.success || res.data.code === 0) {
                message.success('规则同步成功');
                fetchRules(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.error || '同步失败');
            }
        } catch (error) {
            message.error('同步失败: ' + (error.response?.data?.error || error.message));
        }
        setSyncing(false);
    };

    // Filter rules columns
    const filterColumns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        {
            title: '名称',
            dataIndex: 'name',
            render: (text, record) => (
                <a onClick={() => handleView(record)}><strong>{text}</strong></a>
            ),
        },
        {
            title: '描述',
            dataIndex: 'description',
            width: 180,
            ellipsis: true,
            render: (desc) => desc?.String || desc || '-',
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            width: 80,
            sorter: (a, b) => a.priority - b.priority,
            render: (p) => <Tag color={p < 50 ? 'red' : p < 100 ? 'orange' : 'blue'}>{p}</Tag>,
        },
        {
            title: '方向',
            dataIndex: 'direction',
            width: 80,
            render: (dir) => {
                const colors = { inbound: 'blue', outbound: 'green', both: 'purple' };
                const labels = { inbound: '入站', outbound: '出站', both: '双向' };
                return <Tag color={colors[dir]}>{labels[dir]}</Tag>;
            },
        },
        {
            title: '范围',
            dataIndex: 'scope',
            width: 100,
            render: (scope, record) => {
                const labels = { global: '全局', domain: '域名', user: '用户' };
                const scopeValue = record.scope_value?.String || record.scope_value;
                return (
                    <Tooltip title={scopeValue}>
                        <Tag>{labels[scope] || scope}</Tag>
                        {scopeValue && <span style={{ fontSize: 11, color: '#999' }}>{scopeValue}</span>}
                    </Tooltip>
                );
            },
        },
        {
            title: '启用',
            dataIndex: 'enabled',
            width: 70,
            render: (enabled, record) => (
                <Switch checked={enabled} size="small" onChange={() => handleToggleEnabled(record)} />
            ),
        },
        {
            title: '终止',
            dataIndex: 'stop_processing',
            width: 60,
            render: (stop) => stop ? <Tag color="red">是</Tag> : <Tag>否</Tag>,
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 160,
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
        {
            title: '操作',
            width: 140,
            render: (_, record) => (
                <Space>
                    <Tooltip title="查看详情">
                        <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
                    </Tooltip>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm title="确定删除此规则吗？" onConfirm={() => handleDelete(record.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Rate limit columns
    const rateLimitColumns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        {
            title: '名称',
            dataIndex: 'name',
            render: (text, record) => (
                <a onClick={() => handleView(record)}><strong>{text}</strong></a>
            ),
        },
        {
            title: '描述',
            dataIndex: 'description',
            width: 150,
            ellipsis: true,
            render: (desc) => desc?.String || desc || '-',
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            width: 70,
            sorter: (a, b) => a.priority - b.priority,
        },
        {
            title: '限制类型',
            dataIndex: 'limit_type',
            width: 90,
            render: (type) => {
                const labels = { ip: 'IP', sender: '发件人', domain: '域名', recipient: '收件人' };
                const colors = { ip: 'blue', sender: 'green', domain: 'purple', recipient: 'orange' };
                return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
            },
        },
        {
            title: '限制 (分/时/天)',
            width: 130,
            render: (_, record) => (
                <span style={{ fontSize: 12 }}>
                    {record.limit_per_minute || '-'} / {record.limit_per_hour || '-'} / {record.limit_per_day || '-'}
                </span>
            ),
        },
        {
            title: '封禁时长',
            dataIndex: 'block_duration',
            width: 90,
            render: (sec) => {
                if (!sec) return '-';
                if (sec >= 86400) return `${Math.floor(sec / 86400)}天`;
                if (sec >= 3600) return `${Math.floor(sec / 3600)}小时`;
                if (sec >= 60) return `${Math.floor(sec / 60)}分钟`;
                return `${sec}秒`;
            },
        },
        {
            title: '启用',
            dataIndex: 'enabled',
            width: 70,
            render: (enabled, record) => (
                <Switch checked={enabled} size="small" onChange={() => handleToggleEnabled(record)} />
            ),
        },
        {
            title: '操作',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Tooltip title="查看详情">
                        <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
                    </Tooltip>
                    <Tooltip title="实时命中">
                        <Button size="small" icon={<DashboardOutlined />} onClick={() => handleViewRateLimitStats(record)} />
                    </Tooltip>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm title="确定删除此规则吗？" onConfirm={() => handleDelete(record.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const renderFilterForm = () => (
        <>
            <Form.Item name="name" label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
                <Input placeholder="如：Block Spam" />
            </Form.Item>
            <Form.Item name="description" label="描述">
                <Input placeholder="规则说明" />
            </Form.Item>
            <Space style={{ width: '100%' }}>
                <Form.Item name="priority" label="优先级" rules={[{ required: true }]} style={{ width: 150 }}>
                    <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="direction" label="方向" rules={[{ required: true }]} style={{ width: 120 }}>
                    <Select options={directionOptions} />
                </Form.Item>
                <Form.Item name="scope" label="范围" rules={[{ required: true }]} style={{ width: 100 }}>
                    <Select options={scopeOptions} />
                </Form.Item>
            </Space>
            <Form.Item name="scope_value" label="范围值" extra="如果范围是域名或用户，填写具体的域名或邮箱">
                <Input placeholder="如：example.com" />
            </Form.Item>
            <Form.Item
                name="condition"
                label={
                    <Space>
                        条件 (JSON)
                        <Tooltip title={<pre style={{ fontSize: 11, margin: 0 }}>{conditionHelp}</pre>}>
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                }
            >
                <TextArea rows={5} placeholder='{"OpLogic":"&&","Conditions":[...]}' style={{ fontFamily: 'monospace' }} />
            </Form.Item>
            <Form.Item
                name="actions"
                label={
                    <Space>
                        动作 (JSON)
                        <Tooltip title={<pre style={{ fontSize: 11, margin: 0 }}>{actionsHelp}</pre>}>
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Space>
                }
            >
                <TextArea rows={4} placeholder='[{"type":"reject","params":{...}}]' style={{ fontFamily: 'monospace' }} />
            </Form.Item>
            <Space>
                <Form.Item name="stop_processing" valuePropName="checked">
                    <Switch checkedChildren="终止处理" unCheckedChildren="继续处理" />
                </Form.Item>
                <Form.Item name="enabled" valuePropName="checked">
                    <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                </Form.Item>
            </Space>
        </>
    );

    const renderRateLimitForm = () => (
        <>
            <Form.Item name="name" label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
                <Input placeholder="如：IP Rate Limit" />
            </Form.Item>
            <Form.Item name="description" label="描述">
                <Input placeholder="规则说明" />
            </Form.Item>
            <Space style={{ width: '100%' }}>
                <Form.Item name="priority" label="优先级" rules={[{ required: true }]} style={{ width: 120 }}>
                    <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="limit_type" label="限制类型" rules={[{ required: true }]} style={{ width: 130 }}>
                    <Select options={limitTypeOptions} />
                </Form.Item>
                <Form.Item name="direction" label="方向" rules={[{ required: true }]} style={{ width: 100 }}>
                    <Select options={directionOptions} />
                </Form.Item>
            </Space>
            <Space style={{ width: '100%' }}>
                <Form.Item name="scope" label="范围" rules={[{ required: true }]} style={{ width: 100 }}>
                    <Select options={scopeOptions} />
                </Form.Item>
                <Form.Item name="scope_value" label="范围值" style={{ flex: 1 }}>
                    <Input placeholder="如果范围是域名或用户，填写具体值" />
                </Form.Item>
            </Space>
            <Form.Item name="condition" label="条件 (JSON)" extra="可选，用于匹配特定条件">
                <TextArea rows={2} placeholder='{"$eq": ["$.is_new_sender", true]}' style={{ fontFamily: 'monospace' }} />
            </Form.Item>
            <Form.Item label="速率限制">
                <Space>
                    <Form.Item name="limit_per_minute" noStyle>
                        <InputNumber min={0} placeholder="每分钟" addonAfter="/分" style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name="limit_per_hour" noStyle>
                        <InputNumber min={0} placeholder="每小时" addonAfter="/时" style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name="limit_per_day" noStyle>
                        <InputNumber min={0} placeholder="每天" addonAfter="/天" style={{ width: 120 }} />
                    </Form.Item>
                </Space>
            </Form.Item>
            <Form.Item name="exceed_action" label="超限动作 (JSON)">
                <TextArea rows={2} placeholder='{"type":"defer","params":{"code":451,"message":"Rate limit exceeded"}}' style={{ fontFamily: 'monospace' }} />
            </Form.Item>
            <Space>
                <Form.Item name="block_duration" label="封禁时长 (秒)">
                    <InputNumber min={0} style={{ width: 150 }} />
                </Form.Item>
                <Form.Item name="enabled" valuePropName="checked">
                    <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                </Form.Item>
            </Space>
        </>
    );

    const renderRuleDetail = () => {
        if (!viewingRule) return null;
        const isFilter = activeTab === 'filter';

        return (
            <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="ID">{viewingRule.id}</Descriptions.Item>
                <Descriptions.Item label="名称">{viewingRule.name}</Descriptions.Item>
                <Descriptions.Item label="描述" span={2}>{viewingRule.description?.String || viewingRule.description || '-'}</Descriptions.Item>
                <Descriptions.Item label="优先级">{viewingRule.priority}</Descriptions.Item>
                <Descriptions.Item label="方向">
                    {({ inbound: '入站', outbound: '出站', both: '双向' })[viewingRule.direction]}
                </Descriptions.Item>
                <Descriptions.Item label="范围">{({ global: '全局', domain: '域名', user: '用户' })[viewingRule.scope]}</Descriptions.Item>
                <Descriptions.Item label="范围值">{viewingRule.scope_value?.String || viewingRule.scope_value || '-'}</Descriptions.Item>
                <Descriptions.Item label="启用状态">{viewingRule.enabled ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>}</Descriptions.Item>
                {isFilter && (
                    <Descriptions.Item label="终止处理">{viewingRule.stop_processing ? <Tag color="red">是</Tag> : <Tag>否</Tag>}</Descriptions.Item>
                )}
                {!isFilter && (
                    <>
                        <Descriptions.Item label="限制类型">
                            {({ ip: 'IP', sender: '发件人', domain: '域名', recipient: '收件人' })[viewingRule.limit_type]}
                        </Descriptions.Item>
                        <Descriptions.Item label="每分钟限制">{viewingRule.limit_per_minute || '-'}</Descriptions.Item>
                        <Descriptions.Item label="每小时限制">{viewingRule.limit_per_hour || '-'}</Descriptions.Item>
                        <Descriptions.Item label="每天限制">{viewingRule.limit_per_day || '-'}</Descriptions.Item>
                        <Descriptions.Item label="封禁时长">{viewingRule.block_duration ? `${viewingRule.block_duration}秒` : '-'}</Descriptions.Item>
                    </>
                )}
                <Descriptions.Item label="创建时间">{viewingRule.created_at ? new Date(viewingRule.created_at).toLocaleString() : '-'}</Descriptions.Item>
                <Descriptions.Item label="更新时间">{viewingRule.updated_at ? new Date(viewingRule.updated_at).toLocaleString() : '-'}</Descriptions.Item>
                <Descriptions.Item label={isFilter ? '条件' : '条件 (可选)'} span={2}>
                    <pre style={{ margin: 0, fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                        {typeof viewingRule.condition === 'object'
                            ? JSON.stringify(viewingRule.condition, null, 2)
                            : (viewingRule.condition || '-')}
                    </pre>
                </Descriptions.Item>
                {isFilter && (
                    <Descriptions.Item label="动作" span={2}>
                        <pre style={{ margin: 0, fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                            {typeof viewingRule.actions === 'object'
                                ? JSON.stringify(viewingRule.actions, null, 2)
                                : (viewingRule.actions || '-')}
                        </pre>
                    </Descriptions.Item>
                )}
                {!isFilter && (
                    <Descriptions.Item label="超限动作" span={2}>
                        <pre style={{ margin: 0, fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
                            {typeof viewingRule.exceed_action === 'object'
                                ? JSON.stringify(viewingRule.exceed_action, null, 2)
                                : (viewingRule.exceed_action || '-')}
                        </pre>
                    </Descriptions.Item>
                )}
            </Descriptions>
        );
    };

    return (
        <Card
            title={
                <Space>
                    <FilterOutlined />
                    邮件规则管理
                </Space>
            }
            extra={
                <Space>
                    <Select
                        style={{ width: 180 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Tooltip title="同步规则到 Redis">
                        <Button icon={<SyncOutlined spin={syncing} />} onClick={handleSyncRules} loading={syncing}>
                            同步规则
                        </Button>
                    </Tooltip>
                    <Button icon={<ReloadOutlined />} onClick={() => fetchRules(pagination.current, pagination.pageSize)}>刷新</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedCluster}>
                        添加{activeTab === 'filter' ? '过滤规则' : '限速规则'}
                    </Button>
                </Space>
            }
        >
            {!selectedCluster && (
                <Alert message="请先选择一个邮件集群" type="warning" showIcon style={{ marginBottom: 16 }} />
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane
                    tab={<span><FilterOutlined /> 过滤规则</span>}
                    key="filter"
                >
                    <Table
                        columns={filterColumns}
                        dataSource={rules}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: <Empty description={selectedCluster ? '暂无过滤规则，点击右上角“添加过滤规则”创建' : '请先选择集群'} /> }}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 条`,
                            onChange: (page, pageSize) => fetchRules(page, pageSize),
                        }}
                    />
                </TabPane>
                <TabPane
                    tab={<span><ThunderboltOutlined /> 限速规则</span>}
                    key="ratelimit"
                >
                    <Table
                        columns={rateLimitColumns}
                        dataSource={rules}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: <Empty description={selectedCluster ? '暂无限速规则，点击右上角“添加限速规则”创建' : '请先选择集群'} /> }}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 条`,
                            onChange: (page, pageSize) => fetchRules(page, pageSize),
                        }}
                    />
                </TabPane>
            </Tabs>

            <Modal
                title={editingRule ? `编辑${activeTab === 'filter' ? '过滤' : '限速'}规则` : `添加${activeTab === 'filter' ? '过滤' : '限速'}规则`}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={700}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {activeTab === 'filter' ? renderFilterForm() : renderRateLimitForm()}
                    <Form.Item style={{ marginTop: 16 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer
                title={`${activeTab === 'filter' ? '过滤' : '限速'}规则详情 - ${viewingRule?.name || ''}`}
                open={detailDrawerVisible}
                onClose={() => setDetailDrawerVisible(false)}
                width={600}
                extra={
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => { setDetailDrawerVisible(false); handleEdit(viewingRule); }}>
                            编辑
                        </Button>
                    </Space>
                }
            >
                {renderRuleDetail()}
            </Drawer>

            {/* Rate limit realtime hit stats */}
            <Modal
                title={`实时命中 - ${rlStatsRecord?.name || ''}`}
                open={rlStatsModalVisible}
                onCancel={() => setRlStatsModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setRlStatsModalVisible(false)}>关闭</Button>,
                ]}
                width={560}
            >
                <Space style={{ width: '100%', marginBottom: 16 }}>
                    <Input
                        placeholder="限速键，如 ip:1.2.3.4 / example.com"
                        value={rlStatsKey}
                        onChange={(e) => setRlStatsKey(e.target.value)}
                        onPressEnter={() => fetchRateLimitStats(rlStatsKey)}
                        style={{ width: 340 }}
                    />
                    <Button type="primary" icon={<ReloadOutlined />} loading={rlStatsLoading} onClick={() => fetchRateLimitStats(rlStatsKey)}>
                        查询
                    </Button>
                </Space>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="限速键由限制类型与其值组成（如 IP、发件人、域名）。填写要查询的具体键以查看当前时间窗内的命中计数。"
                />
                <Spin spinning={rlStatsLoading}>
                    {rlStatsData ? (
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={8}>
                                <Card size="small"><Statistic title="本分钟" value={rlStatsData.minute ?? rlStatsData.per_minute ?? rlStatsData.count_minute ?? 0} /></Card>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Card size="small"><Statistic title="本小时" value={rlStatsData.hour ?? rlStatsData.per_hour ?? rlStatsData.count_hour ?? 0} /></Card>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Card size="small"><Statistic title="今日" value={rlStatsData.day ?? rlStatsData.per_day ?? rlStatsData.count_day ?? 0} /></Card>
                            </Col>
                            <Col xs={24}>
                                <Card size="small">
                                    <Space>
                                        <span>封禁状态:</span>
                                        {rlStatsData.blocked ? <Tag color="red">已封禁</Tag> : <Tag color="green">正常</Tag>}
                                        {rlStatsData.blocked_until && (
                                            <span style={{ color: '#999', fontSize: 12 }}>至 {new Date(rlStatsData.blocked_until).toLocaleString()}</span>
                                        )}
                                    </Space>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                        !rlStatsLoading && <Empty description="输入限速键并查询" />
                    )}
                </Spin>
            </Modal>
        </Card>
    );
};

export default Rules;
